<template>
  <a-modal
    :visible="visible"
    width="520px"
    class="modal_title"
    :title="record.channel_id === ChannelType.Sms ? 'Solicitud de canal SMS' : 
      record.channel_id === ChannelType.VoiceInbound ? 
      'Solicitud de número entrante' : `Solicitud del número “${record.data_parameters?.phone_number}”`"
    :bodyStyle="{ padding: '20px 0px 40px 0' }"
    centered
    @cancel="emit('update:visible', false)"
  >
    <a-divider class="divider" />
    <CustomScrollbar height="550px" auto>
      <p>
        <strong>Estado: </strong>
        <a-tag :color="getStatusColor(record)">{{ getStatusText(record) }}</a-tag>
      </p>
      <p>
        <strong>Asunto: </strong>
        {{ subject }}
      </p>
      <p>
        <strong>Fecha de envío de la solicitud: </strong>
        {{ dayjs(record.created_at).format("DD/MM/YYYY [a las] HH:mm") }}
      </p>

      <div v-if="isWhatsappChannel">
        <h4>Canales solicitados: </h4>
        <ul>
          <li v-for="name in whatsappChannelsName" :key="name">
            {{ name }}
          </li>
        </ul>
      </div>

      <h4>Datos de contacto</h4>

      <p>
        <strong>Nombres: </strong>
        {{  personalData.fullname }}
        <!-- {{ record.data_parameters?.sc_user.first_name }} -->
      </p>
      <!-- <p>
        <strong>Apellidos: </strong>
        {{ record.data_parameters?.sc_user.last_name || "-" }}
      </p> -->
      <p>
        <strong>Correo electrónico: </strong>
        {{ personalData.email }}
      </p>
      <p>
        <strong>Teléfono de contacto: </strong>
        <!-- {{ record.data_parameters?.sc_user.phone || "-" }} -->
        {{ personalData.phone }}
      </p>

      <div v-if="isVoiceInboundChannel">
        <h4>Datos de la empresa</h4>

        <p>
          <strong>Nombre: </strong>
          {{ record.data_parameters?.company_name || "-" }}
        </p>

        <p v-if="industry">
          <strong>Industria: </strong>
          {{ industry.name }}
        </p>

        <p v-if="record.data_parameters?.company_language">
          <strong>Idioma: </strong>
          {{ record.data_parameters?.company_language }}
        </p>

        <p v-if="country">
          <strong>País: </strong>
          {{ country.name }}
        </p>

        <p v-if="city">
          <strong>Ciudad: </strong>
          {{ city.name }}
        </p>

        <p v-if="record.data_parameters?.address">
          <strong>Dirección: </strong>
          {{ record.data_parameters?.address }}
        </p>

        <p v-if="record.data_parameters?.website">
          <strong>Página web: </strong>
          {{ record.data_parameters?.website }}
        </p>
      </div>
    </CustomScrollbar>
    <template #footer>
      <div style="text-align: center" class="footer">
        <a-button key="back" @click="emit('update:visible', false)">Cerrar detalles</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import dayjs from "dayjs";
import { useStore } from "vuex";

import { ChannelCustomer } from "@/@types/global";
import { channelStatus } from "@/app/channels/utils/constants";
import { ChannelType } from "@/app/shared/utils/enums";
import { cityClient } from "@/app/workspace/services";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const hasWhatsappChannel = [ChannelType.Whatsapp, ChannelType.WhatsappVoice] as number[];
const hasSmsChannel = [ChannelType.Sms, ...hasWhatsappChannel] as number[];
const hasVoiceInboundChannel = [ChannelType.VoiceInbound, ...hasSmsChannel] as number[];

const isWhatsappChannel = computed(() => hasWhatsappChannel.includes(props.record.channel_id));
const isSmsChannel = computed(() => hasSmsChannel.includes(props.record.channel_id));
const isVoiceInboundChannel = computed(() => ![...hasVoiceInboundChannel].includes(props.record.channel_id));

const props = defineProps<{
  visible: boolean;
  record: ChannelCustomer;
}>();

const emit = defineEmits<{
  "update:visible": [visible: boolean];
}>();

const store = useStore();

const getStatusColor = (record: ChannelCustomer) => {
  if (record.status === channelStatus.active) {
    return "green";
  }
  if (record.status === channelStatus.inactive || record.status === channelStatus.reject) {
    return "red";
  }
  if (record.status === channelStatus.pending) {
    return "gold";
  }
  return "default";
};

const getStatusText = (record: ChannelCustomer) => {
  if (record.status === channelStatus.active) {
    return "Activo";
  }
  if (record.status === channelStatus.inactive) {
    return "Inactivo";
  }
  if (record.status === channelStatus.reject) {
    return "Rechazado";
  }
  if (record.status === channelStatus.incomplete) {
    return "Incompleto"
  }
  return "Pendiente";
};

const subject = computed(() => {
  if (props.record.channel_id === ChannelType.Sms)
    return `Integración de canal de SMS.`

  if (props.record.channel_id === ChannelType.VoiceInbound)
    return `Integración de un número entrante conectado a la troncal de ${props.record.provider_name}`;

  if (isWhatsappChannel)
    return `Integración de WhatsApp Business Api con ${props.record.provider_name}`;

  return "";
});

const country = computed(() =>
  store.getters["getAllCountries"].find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (country: any) => country.id === props.record.data_parameters?.country
  )
);

const personalData = computed(() => {
  if (props.record.channel_id === ChannelType.VoiceInbound) {
    return {
      fullname: props.record.data_parameters?.contact_name || props.record.data_parameters?.sc_user?.fullname || "-",
      email: props.record.data_parameters?.contact_email || props.record.data_parameters?.sc_user.email || "-",
      phone: props.record.data_parameters?.phone_number || props.record.data_parameters?.sc_user.phone || "-",
    }
  } else if (isSmsChannel) {
    return {
      fullname: props.record.data_parameters?.contact_name || "-",
      email: props.record.data_parameters?.contact_email || "-",
      phone: props.record.data_parameters?.phone_number || "-",
    }
  } else {
    return {
      fullname: props.record.data_parameters?.sc_user?.fullname || "-",
      email: props.record.data_parameters?.sc_user.email || "-",
      phone: props.record.data_parameters?.sc_user.phone || "-",
    }
  }
});

const industry = computed(() =>
  store.getters["getAllIndustries"].find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (industry: any) => industry.id === props.record.data_parameters?.type_industry
  )
);

const whatsappChannelsName = computed(() => {
  const listChannels: string[] = []
  if (isWhatsappChannel) {
    listChannels.push('WhatsApp Message')
    if (props.record.data_parameters?.enable_voice) {
      listChannels.push('WhatsApp Voice')
    }
  }
  return listChannels
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const city = ref<any>();

watch(
  country,
  async (newCountry) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cities = (await cityClient(newCountry.id)) as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    city.value = cities.find((c: any) => c.id === props.record.data_parameters?.city);
  },
  {
    immediate: true,
  }
);
</script>

<style scoped lang="sass">
p
  margin-bottom: 3px
  color: $gray-8
  strong
    font-weight: 600

li
  color: $gray-8

h4
  margin: 20px 0 5px
  color: $gray-8
  font-weight: bolder

.footer
  display: flex
  align-items: center
  justify-content: center
  padding: 10px 16px
  border-top: $border-normal
  position: absolute
  width: 100%
  bottom: 0
  left: 0
.divider
  position: absolute
  left: 0
  top: 45px
  margin: 10px 0
</style>
